<template>
  <div class="tags" v-if="items.length > 1">
    <div class="row no-wrap items-center q-col-gutter-sm">
      <div
          :class="allCellClass"
          v-if="modelValue.length"
      >
        <div>
          <q-chip
              class="cursor-pointer bg-white s-font-2xs s-font-md-xs q-ml-none q-mr-xs q-mr-md-sm q-px-sm q-px-md-sm"
              outline
              color="deep-orange-8"
              clickable
              @click="onReset"
          >
            <span class="gt-sm">
              Показать все
            </span>
            <span class="lt-md">
              Все
            </span>
          </q-chip>
        </div>
      </div>
      <div class="col-shrink">
        <ScrollHorizontal
            :fader-right="true"
            :fader-left="true"
            :hover-scroll="true"
            fader-width="auto"
            fader-color="#fbfbfb"
            class="c-scroll"
            ref="scroller"
        >
          <div class="flex no-wrap items-center">
            <div
                v-if="title && !modelValue.length"
                class="q-mr-sm q-ml-md-sm s-font-2xs text-weight-medium"
            >
              {{ title }}:
            </div>
            <div
                v-for="item of items"
                :key="'selected' + item.ID"
                @click="toggleItem(item)"
            >
              <q-chip
                  v-if="modelValue.includes(item.ID)"
                  class="cursor-pointer text-weight-bold s-font-2xs s-font-md-sm q-ml-none q-mr-xs q-mr-md-sm q-px-sm q-px-md-sm"
                  outline
                  color="primary"
                  @click.native="toggleItem(item)"
                  :ref="(el: any) => setItemRef(el, item)"
              >
                {{ item.NAME }}
              </q-chip>
              <q-chip
                  v-else
                  class="cursor-pointer bg-white s-font-2xs s-font-md-sm q-ml-none q-mr-xs q-mr-md-sm q-px-sm q-px-md-sm"
                  outline
                  :class="item.FEATURED ? 'text-weight-bold' : ''"
                  :color="item.FEATURED ? 'deep-orange-8' : 'grey-6'"
                  @click.native="toggleItem(item)"
                  :ref="(el: any) => setItemRef(el, item)"
              >
                {{ item.NAME }}
              </q-chip>
            </div>
          </div>
        </ScrollHorizontal>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import ScrollHorizontal from "./ScrollHorizontal.vue";

import {QChip, QTabProps, QTabsProps} from "quasar";
import {type ComponentPublicInstance, ref, toRefs} from "vue";
import {ScrollHorizontalApi} from "@/components/Ui/ScrollHorizontal.vue";

export type TagsChipsApi = {
  scrollTo: (tagId: number) => void
  scrollToSelected: () => void
  scrollToFirst: () => void
  scrollToStart: () => void
}

export interface TagItem {
  ID: number
  NAME: string
  FEATURED?: boolean
}

export interface TagsProps {
  title?: string
  items: TagItem[]
  allCellClass?: string
  allTabsProps?: Partial<QTabsProps>
  allTabProps?: Partial<QTabProps>
}

const itemRefsById = ref<Record<string, HTMLElement>>({})

const setItemRef = (el: ComponentPublicInstance<QChip> | null, item: TagItem) => {
  itemRefsById.value[item.ID] = el?.$el
}

const scroller = ref<ScrollHorizontalApi | null>(null)

const props = withDefaults(defineProps<TagsProps>(), {
  allCellClass: 'col-auto',
  allTabProps: () => ({}),
  allTabsProps: () => ({})
})

const {title, allTabProps, allTabsProps} = props

const {items} = toRefs(props)

const _allTabProps: Partial<QTabProps> = {
  noCaps: true,
  ...allTabProps
}

const _allTabsProps: Partial<QTabsProps> = {
  inlineLabel: true,
  dense: true,
  activeColor: "primary",
  align: "left",
  ...allTabsProps
}

const modelValue = defineModel<number[]>({required: true})

const toggleItem = (item: TagItem) => {
  if (modelValue.value.includes(item.ID)) {
    modelValue.value = modelValue.value.filter(tagId => tagId !== item.ID)
  } else {
    modelValue.value.push(item.ID)
  }
}

const onReset = () => {
  modelValue.value = []
  scroller.value?.scrollToStart()
}

const scrollTo: TagsChipsApi['scrollTo'] = (tagId) => {
  const el = itemRefsById.value[tagId]
  if (el) {
    scroller.value?.scrollToElement(el, {
      behavior: 'smooth',
      block: 'center',
    })
  }
}

const scrollToSelected: TagsChipsApi['scrollToSelected'] = () => {
  if (modelValue.value.length) {
    const tagId = modelValue.value[0]
    if (tagId) {
      scrollTo(tagId)
    }
  }
}

const scrollToFirst: TagsChipsApi['scrollToFirst'] = () => {
  const item = items.value[0]
  if (item) {
    scrollTo(item.ID)
  }
}

const scrollToStart: TagsChipsApi['scrollToStart'] = () => {
  scroller.value?.scrollToStart()
}

defineExpose<TagsChipsApi>({
  scrollTo,
  scrollToSelected,
  scrollToFirst,
  scrollToStart
})

</script>

<style lang="scss" scoped>

.c-scroll {
  :deep(.fade-layer) {
    width: 20px !important;
  }
}

@media (min-width: $breakpoint-md-min) {
  .c-scroll {
    :deep(.fade-layer) {
      width: 30px !important;
    }
  }
}

</style>
